import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SiteFormDetail, SiteFormSummary } from '../models/site.model';
import { BaseService } from '@src/app/shared/services/base.service';

export interface SiteFormOptionGroup {
    serviceId: number | null;
    label: string;
    forms: SiteFormSummary[];
}

@Injectable({ providedIn: 'root' })
export class SiteFormsService extends BaseService {
    private static readonly UNASSIGNED_LABEL = 'Unassigned Service';

    public constructor(private readonly httpClient: HttpClient) {
        super();
    }

    public loadFormGroups(): Observable<SiteFormOptionGroup[]> {
        const params = new HttpParams()
            .set('include', 'service')
            .set('per_page', '500');

        return this.httpClient
            .get<{ data?: SiteFormSummary[] }>(`${this.apiUrl}/forms`, { params })
            .pipe(map((response) => this.groupForms(response?.data ?? [])));
    }

    public loadFormDetails(formId: string): Observable<SiteFormDetail> {
        return this.httpClient
            .get<{data: SiteFormDetail}>(`${this.apiUrl}/forms/${formId}`)
            .pipe(map((response) => response.data));
    }

    private groupForms(forms: SiteFormSummary[]): SiteFormOptionGroup[] {
        const groups = forms.reduce<Record<string, SiteFormOptionGroup>>((acc, form) => {
            const label = (form.service?.name ?? SiteFormsService.UNASSIGNED_LABEL).trim();

            if (!acc[label]) {
                acc[label] = {
                    serviceId: form.service?.id ?? null,
                    label,
                    forms: [],
                };
            }

            acc[label].forms.push(form);
            return acc;
        }, {});

        return Object.values(groups)
            .map((group) => ({
                ...group,
                forms: [...group.forms].sort((a, b) => a.title.localeCompare(b.title)),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }
}
